import React from 'react';
import { useSelector } from 'react-redux';
import { ArrayHelpers, Forms, Loading, SystemText } from 'e4shared';
import Filters from '../_filters';
import GroupPicker from 'components/admin/forms/groupPicker';
import Board from './board';
import WidgetBase, { groupSet, widgetLoad, widgetLoadRecommendations } from '../_base';
import MetricRecommendations from '../components/metricRecommendations';
import { apiGet } from 'services';
import Navigation from '../_navigation';
import PeriodPicker from 'components/admin/forms/periodPicker';
import { translateArea, translateCompetency } from 'helpers/translations';

export const CompetencyGrid = ({ defaultProps, id, item, setItem, onDelete }) => {
    const showFilters = useSelector(state => state.ui.filterWidget);
    const user = useSelector(state => state.user.user);
    const [values, setValues] = React.useState({});
    const [errors, setErrors] = React.useState({});
    const [areas, setAreas] = React.useState([]);
    const [notConfigured, setNotConfigured] = React.useState(false);
    const [loading, setLoading] = React.useState(true);
    const [recLoading, setRecLoading] = React.useState(true);
    const [data, setData] = React.useState([]);
    const [recommendationData, setRecommendationData] = React.useState({});
    const [area, setArea] = React.useState(0);

    const defaults = { period: 'M' };

    React.useEffect(() => {
        const parsed = JSON.parse(item.data);
        setValues({ ...defaults, ...parsed });
    }, [item?.data]);

    React.useEffect(() => {
        load();
    }, [area]);

    const load = () => {
        apiGet(`competency-area`, user.guid, response => {
            setAreas(response.map(a => translateArea(user.language_Code, a)));
            return response;
        }).then(response => {
            const qsParams = `?id=${availableAreas(response)[area]?.id}`;
            widgetLoad(
                item.id,
                user.guid,
                validator,
                setLoading,
                setNotConfigured,
                d => setData(d.map(c => translateCompetency(user.language_Code, c))),
                () => {
                    widgetLoadRecommendations(item.id, user.guid, setRecLoading, setRecommendationData, qsParams);
                },
                null,
                qsParams
            );
        });
    };

    const updateProperty = (value, prop) => {
        const temp = { ...values };
        temp[prop] = value;
        setValues(temp);
    };

    const height = defaultProps.height(item);

    const validator = suppressErrors => {
        const form = Object.keys(values).length ? values : JSON.parse(item.data);
        const errors = {};
        let validForm = true;

        if (!form.name) {
            errors.name = SystemText('Dashboard/Widgets/General/Edit/Fields/Name/Error');
            validForm = false;
        }

        if (!groupSet(form.group1)) {
            errors.group1 = SystemText('Dashboard/Widgets/CompetencyGrid/Fields/Group/Error');
            validForm = false;
        }

        if (!ArrayHelpers.existsAndHasLength(form.options)) {
            errors.options = SystemText('Dashboard/Widgets/CompetencyGrid/Fields/Areas/Error');
            validForm = false;
        }

        if (!form.period) {
            errors.period = SystemText('Dashboard/Widgets/CompetencyGrid/Fields/Period/Error');
            validForm = false;
        }

        if (!suppressErrors) setErrors(errors);
        return validForm;
    };

    const nav = dir => {
        let a = area;
        a += dir;
        a = a < 0 ? availableAreas().length - 1 : a;
        a = a >= availableAreas().length ? 0 : a;
        setArea(a);
    };

    const availableAreas = React.useCallback(
        override => {
            const parsed = JSON.parse(item.data);
            return (override || areas)?.filter(a => parsed.options?.some(x => x === a.id));
        },
        [item.data, areas]
    );

    const chart = (
        <div className="chart-container" id={`chart-${item.id}`} style={{ height: `${height}px` }}>
            <div className="chart" style={{ height: `${height}px` }}>
                <Board data={data} item={item} id={`chart-${item.id}`} />
            </div>
        </div>
    );

    const recommendations = () =>
        recLoading ? <Loading /> : <MetricRecommendations competencies={recommendationData?.results} />;

    const filters = values ? (
        <Filters
            validator={validator}
            widget={item}
            values={values}
            errors={errors}
            onChange={(value, prop) => updateProperty(value, prop)}
            refresh={x => {
                setItem(x);
                load();
            }}
            show={showFilters === id}
            onDelete={onDelete}
        >
            <GroupPicker
                mandatory
                allowMultiple
                value={values.group1}
                error={errors.group1}
                onChange={value => updateProperty(value, 'group1')}
                showUserOption={defaultProps.company}
                controlId={id}
            />
            <Forms.Select
                label={SystemText('Dashboard/Widgets/CompetencyGrid/Fields/Areas/Label')}
                data={areas}
                value={values.options}
                mandatory
                onChange={value => updateProperty(value, 'options')}
                allowMultiple
                error={errors.options}
                help={SystemText('Dashboard/Widgets/CompetencyGrid/Fields/Areas/Help')}
                controlId={`${id}-options`}
            />
            <PeriodPicker
                value={values.period}
                error={errors.period}
                onChange={value => updateProperty(value, 'period')}
                help={SystemText('Dashboard/Widgets/CompetencyGrid/Fields/Period/Help')}
            />
        </Filters>
    ) : null;

    const navigation =
        availableAreas()?.length > 1 ? <Navigation nav={nav} label={availableAreas()[area]?.name} id={id} /> : null;

    return (
        <WidgetBase
            filters={filters}
            navigation={navigation}
            chart={chart}
            height={height}
            item={item}
            recommendations={recommendations()}
            id={id}
            help={null}
            hideConfig={defaultProps.company && !user.administrator}
            extra={item.extra}
            loading={loading}
            notConfigured={notConfigured}
        />
    );
};

export default CompetencyGrid;
